/**
 * Edit a connected account's editable metadata — display name + external account
 * id. Credentials are NOT edited here (that's Reconnect); this only touches the
 * fields that don't require re-auth.
 *
 * The account id is normalized per platform (same rule as the connect form) so a
 * hyphenated Google Customer ID or a bare Facebook id is stored the way the sync
 * client expects. A duplicate id surfaces the service's friendly 23505 message.
 *
 * @module components/settings/EditAccountModal
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { clsx } from "clsx";
import { Modal } from "@/components/Modal";
import { MANUAL_METHODS, DEFAULT_MANUAL } from "./manualMethods";
import type { PlatformAccount } from "@/types";

export interface EditAccountModalProps {
  account: PlatformAccount | null;
  isOpen: boolean;
  onClose: () => void;
  /** persist the edit; may throw — the thrown message is shown inline */
  onSave: (updates: { accountName: string; accountId: string }) => Promise<void>;
}

export const EditAccountModal: React.FC<EditAccountModalProps> = ({
  account,
  isOpen,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form whenever a different account is opened for editing.
  useEffect(() => {
    if (account) {
      setName(account.accountName ?? "");
      setAccountId(account.accountId ?? "");
      setError(null);
      setSaving(false);
    }
  }, [account]);

  if (!account) return null;

  const cfg = MANUAL_METHODS[account.platform] ?? DEFAULT_MANUAL;

  // Editing the id to a genuinely different value (after normalization) on an
  // account that already has synced data would leave that data mislabeled under
  // this connection — the right move is disconnect + reconnect. The common
  // typo/format fix normalizes back to the same id, so it never trips this.
  const idChanged = cfg.normalizeAccountId(accountId.trim()) !== account.accountId;
  const warnDifferentAccount = idChanged && Boolean(account.lastSyncAt);

  const handleSave = async (): Promise<void> => {
    if (saving) return;
    const trimmedName = name.trim();
    const normalizedId = cfg.normalizeAccountId(accountId.trim());
    if (!trimmedName) {
      setError(t("settings.pleaseEnterAccountName"));
      return;
    }
    if (!normalizedId) {
      setError(t('settings.pleaseEnterThe', { field: t(cfg.accountLabel) }));
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({ accountName: trimmedName, accountId: normalizedId });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("settings.errSaveChanges"));
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('settings.editAccount')}
      description={t('settings.editAccountHint')}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim() || !accountId.trim()}
            className={clsx(
              "inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg",
              "bg-primary-600 text-ink-950 hover:bg-primary-700",
              "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ink-950",
              "transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                {t('settings.saving')}
              </>
            ) : (
              t('settings.saveChanges')
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="edit-account-name" className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.accountNameLabel')}
          </label>
          <input
            id="edit-account-name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            placeholder={t('settings.accountNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="edit-account-id" className="block text-sm font-medium text-gray-700 mb-1">
            {t(cfg.accountLabel)}
          </label>
          <input
            id="edit-account-id"
            type="text"
            value={accountId}
            onChange={(e) => { setAccountId(e.target.value); setError(null); }}
            placeholder={t(cfg.accountPlaceholder)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
          />
          <p className="mt-1 text-xs text-gray-600">
            {t("settings.editHintRepoint")}
          </p>
        </div>

        {warnDifferentAccount && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            {t("settings.editWarnDifferent")}
          </div>
        )}

        {error && (
          <div className="p-3 bg-error-50 border border-error-200 rounded-lg text-error-700 text-sm">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditAccountModal;
